import { ConnectError } from '@connectrpc/connect'
import {
  type BiddingIntradayCsvApplyRejection,
  BiddingIntradayCsvApplyRejectionSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'

/** 確定時の取込全体の拒否 (spec AC-BCI-035) を、 確定モーダル用に取り出したもの。 */
export type IntradayConfirmRejection = {
  /**
   * 行エラー (CSV のデータ行順。 1 行に複数の違反があれば違反ごとに 1 件)。
   * `retryLater` は、 時間をおくと解消しうる行エラー (開場前・基準価格の値が無い。
   * AC-BCI-030 / 032)。
   */
  rowErrors: Array<{ rowNumber: number; message: string; retryLater: boolean }>
  /** 行番号を持たない、 ファイル全体が理由の文言。 */
  fileMessages: string[]
}

/**
 * 確定 (Apply) の失敗が取込全体の拒否なら、 error detail
 * (`BiddingIntradayCsvApplyRejection`) から行エラーとファイル全体の理由を取り出す。
 * detail が無い失敗 (reason で返る拒否・内部エラー等) は undefined を返し、
 * 呼び出し側は通常の失敗として扱う。 error のメッセージの文字列は見ない。
 */
export function readIntradayConfirmRejection(
  err: unknown,
): IntradayConfirmRejection | undefined {
  if (!(err instanceof ConnectError)) return undefined
  let detail: BiddingIntradayCsvApplyRejection | undefined
  try {
    detail = err.findDetails(BiddingIntradayCsvApplyRejectionSchema)[0]
  } catch {
    return undefined
  }
  if (detail === undefined) return undefined
  // 中身の無い detail は出せる理由が無いので、 通常の失敗に倒す。
  if (detail.rowErrors.length === 0 && detail.fileMessages.length === 0) {
    return undefined
  }
  return {
    rowErrors: detail.rowErrors.map(({ rowNumber, message, retryLater }) => ({
      rowNumber,
      message,
      retryLater,
    })),
    fileMessages: [...detail.fileMessages],
  }
}

/**
 * 確定モーダルが案内する次の操作 (案内は原因ごとに 1 つ)。 行エラーがあり、 そのすべてが
 * 時間をおくと解消する原因なら「時間をおいてから、 取り込み直す」、 それ以外 (CSV の誤りが
 * 1 行でもある・ファイル全体の理由だけ) は呼び出し側の既定 (CSV を直す案内、 またはファイル全体の
 * 理由の文言そのもの) に任せる。
 */
export function shouldRetryLater(rejection: IntradayConfirmRejection): boolean {
  return (
    rejection.rowErrors.length > 0 &&
    rejection.rowErrors.every((rowError) => rowError.retryLater)
  )
}
